import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes } from "react-router-dom";
import { rehydrateAuth } from "store/slices/authSlice";
import { UserRole } from "types/user";
import DashboardLayout from "../components/layout/DashboardLayout";
import Appointments from "../pages/Appointments";
import Dashboard from "../pages/Dashboard";
import Finances from "../pages/Finances";
import Login from "../pages/Login";
import Notifications from "../pages/Notifications";
import Profile from "../pages/Profile";
import Services from "../pages/Services";
import AdminReports from "pages/Reports";
import Verification from "pages/Verifications";
import Users from "../pages/Users";
import { RootState } from "../store/store";
import Bookings from "pages/Bookings";
import Customers from "pages/Customers";
import Providers from "pages/Providers";

const ProtectedRoute = ({
  children,
  allowedRoles,
}: {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
}) => {
  const { isAuthenticated, user } = useSelector(
    (state: RootState) => state.auth
  );

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const AppRoutes = () => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  const dispatch = useDispatch();

  useEffect(() => {
    const saved = localStorage.getItem("loggedInUser");
    if (saved) {
      const { user, token, expiresIn } = JSON.parse(saved);
      dispatch(rehydrateAuth({ user, token, expiry: expiresIn }));
    }
  }, [dispatch]);

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<DashboardLayout />}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/services" element={<Services />} />
        <Route path="/appointments" element={<Appointments />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/bookings" element={<Bookings />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/providers" element={<Providers />} />
        <Route path="/reports" element={<AdminReports />} />
        <Route path="/verifications" element={<Verification />} />

        {/* Admin-only routes */}
        <Route
          path="/users"
          element={
            <ProtectedRoute
              allowedRoles={["admin_system", "admin_super", "staff_support"]}
            >
              <Users />
            </ProtectedRoute>
          }
        />
        <Route
          path="/finances"
          element={
            <ProtectedRoute allowedRoles={["admin_super"]}>
              <Finances />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppRoutes;
